import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import {
  ViolationAct,
  ViolationActDecisionRequestBody,
  ViolationActDevice,
  ViolationActModem,
  ViolationActResultRequestBody,
} from './types';

const tagTypes = [
  'ViolationAct',
  'ViolationActs',
  'ViolationActDevices',
  'ViolationActModems',
];

export const violationActsApi = createApi({
  reducerPath: 'violationActsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/violation-acts',
  }),
  tagTypes,
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getViolationActs: builder.query<ViolationAct[], FiltersGrid | void>({
      query: (params = {}) => {
        return {
          url: `/query`,
          params,
        };
      },
      providesTags: () => ['ViolationAct', 'ViolationActs'],
    }),
    getViolationActsI: builder.infiniteQuery<
      ViolationAct[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['ViolationAct', 'ViolationActs'],
    }),
    getViolationActById: builder.query<ViolationAct, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['ViolationAct'],
    }),
    createViolationActs: builder.mutation<ViolationAct, FormData>({
      query: data => ({
        url: '/',
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: result => [
        'ViolationAct',
        { type: 'ViolationAct', id: result?.id },
      ],
    }),
    updateViolationActs: builder.mutation<ViolationAct, FormData>({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
    deleteViolationActsById: builder.mutation<ViolationAct, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'ViolationAct',
        { type: 'ViolationAct', id },
      ],
    }),
    getViolationActDevices: builder.query<ViolationActDevice[], string>({
      query: id => ({
        url: `/${id}/devices`,
      }),
      providesTags: () => ['ViolationActDevices'],
    }),
    getViolationActModems: builder.query<ViolationActModem[], string>({
      query: id => ({
        url: `/${id}/modems`,
      }),
      providesTags: () => ['ViolationActModems'],
    }),
    validateCreate: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: '/validate-create',
        method: 'POST',
        data: formData,
      }),
    }),
    validateUpdate: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: `/${formData.get('id')}/validate-update`,
        method: 'POST',
        data: formData,
      }),
    }),
    validateCreateDevicesAndModems: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: '/validate-devices-and-modems',
        method: 'POST',
        data: formData,
      }),
    }),
    validateUpdateDevicesAndModems: builder.mutation<boolean, FormData>({
      query: formData => ({
        url: `/${formData.get('id')}/validate-devices-and-modems`,
        method: 'POST',
        data: formData,
      }),
    }),
    setViolationActResult: builder.mutation<
      string,
      ViolationActResultRequestBody
    >({
      query: data => ({
        url: `/${data.id}/set-result`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
    setViolationActDecision: builder.mutation<
      string,
      ViolationActDecisionRequestBody
    >({
      query: data => ({
        url: `/${data.id}/set-decision`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
  }),
});

export const {
  useGetViolationActDevicesQuery,
  useGetViolationActsIInfiniteQuery,
  useGetViolationActByIdQuery,
  useCreateViolationActsMutation,
  useUpdateViolationActsMutation,
  useDeleteViolationActsByIdMutation,
  useGetViolationActModemsQuery,
  useValidateCreateMutation,
  useValidateUpdateMutation,
  useValidateCreateDevicesAndModemsMutation,
  useValidateUpdateDevicesAndModemsMutation,
  useSetViolationActDecisionMutation,
  useSetViolationActResultMutation,
} = violationActsApi;
